import { Component, inject, Input, OnInit } from '@angular/core';
import { Match } from '../_shared/models/match.model';
import { IonicModule, ModalController } from '@ionic/angular';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
 
import { Subject, takeUntil } from 'rxjs';
import { MatchService } from '../_core/services/match.service';
import { ManageSubstitutionService } from './manage-substitution.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-manage-substitution',
  templateUrl: './manage-substitution.component.html',
  styleUrls: ['./manage-substitution.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    IonicModule,
    CommonModule,
    TranslateModule,
  ]
})
export class ManageSubstitutionComponent  implements OnInit {
    private destroy$ = new Subject<void>();
    private modalController = inject(ModalController);
    private manageSubstitutionService = inject(ManageSubstitutionService)
    @Input({ required: true }) match: Match;
    private matchService = inject(MatchService);

    allowSubstitutions = false;
    unlimitedSubstitutions = false;

    substitutionOptions = [
      ...Array.from({ length: 10 }, (_, i) => i + 1),
      20, 30, 40, 50, 60, 70, 80, 90, 100, 150, 200
    ];
    form!: FormGroup;

    constructor(private fb: FormBuilder,) { }

    ngOnInit() {
        this.form = this.fb.group({
            perSet: [3, Validators.required],
            perMatch: [9, Validators.required],
        },{ validators: this.substitutionValidator() });

     // Set form values if substitutionPolicy exists
      if (this.match?.substitutionPolicy) {
        this.setFormData();

        if(this.match?.substitutionPolicy?.perMatch > 0 && this.match?.substitutionPolicy?.perSet > 0){
          this.allowSubstitutions = true;
        }
        if(this.match?.substitutionPolicy?.perMatch === 100 && this.match?.substitutionPolicy?.perSet === 100){
          this.unlimitedSubstitutions = true;
          this.unlimitedSubstitutionsToggle();
        }
      }
    }

    unlimitedSubstitutionsToggle(){
        if(this.unlimitedSubstitutions){
            this.form.patchValue({ perMatch: 100, perSet: 100 });
              this.form.get('perMatch')?.disable();
              this.form.get('perSet')?.disable();
        } else {
            this.form.get('perMatch')?.enable();
            this.form.get('perSet')?.enable();
            this.form.patchValue({
                perSet: 3,
                perMatch: 9,
            });
        }
    }

    setFormData() {
      this.form.patchValue({
        perSet: this.match?.substitutionPolicy?.perSet ? this.match?.substitutionPolicy?.perSet : 3,
        perMatch: this.match?.substitutionPolicy?.perMatch ?this.match?.substitutionPolicy?.perMatch : 9,
      });
    }

    dismiss(action = null) {
      this.modalController.dismiss(action);
    }

    save(){
      if (this.form.invalid || (this.form.value.perMatch == 0 && this.form.value.perSet == 0) ) {
        return;
      }
      if(this.allowSubstitutions && this.form.value.perMatch && this.form.value.perSet){
        this.manageSubstitutionService.add(this.match._id, this.form.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
            if(response?.success){
            this.match.substitutionPolicy = response?.match?.substitutionPolicy;
            this.syncSubstitutionPolicy();
            this.matchService.publishScoreUpdated(this.match);
            this.modalController.dismiss(null, 'cancel');
          }
        });
      } else {
        if(this.match?.substitutionPolicy?.perMatch > 0 && this.match?.substitutionPolicy?.perSet > 0){
          this.deleteSub(this.match._id);
        }
      }
    }

    async deleteSub(matchId: any){
      return new Promise<void>((resolve) => {
        this.manageSubstitutionService.remove(matchId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (response) => {
          if(response?.success){
              this.match.substitutionPolicy = response?.match?.substitutionPolicy;
              this.syncSubstitutionPolicy();
              this.allowSubstitutions = false;
          }
          this.matchService.publishScoreUpdated(this.match);
          await this.setFormData();
          this.modalController.dismiss(null, 'cancel');
          resolve();
        });
      });
    }

    // Custom validator to ensure perMatch >= perSet
    substitutionValidator(): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
          const perSet = formGroup.get('perSet')?.value;
          const perMatch = formGroup.get('perMatch')?.value;
    
          if (
            perSet &&
            perMatch &&
            parseInt(perMatch, 10) < parseInt(perSet, 10)
          ) {
            return { substitutionMismatch: true }; // Return an error if the condition is not met
          }
          return null; // Return null if the validation passes
        };
    }

    /**
     * Whenever the substitution policy is updated, this method ensures that
     * the substitution track is synchronized with the new policy.
     * It checks if the current substitution track entries match the new policy
     * and updates them if necessary.
     * This is important to maintain consistency in the match's substitution records.
     * @returns void     
     */
    syncSubstitutionPolicy() {
      const substitutePolicy = this.match?.substitutionPolicy;
      if (!substitutePolicy) return;

      const substituteTrack = {
        side1: this.match.substituteTrack?.side1 || [],
        side2: this.match.substituteTrack?.side2 || [],
      };

      const isDifferent = ['side1', 'side2'].some(side =>
        substituteTrack[side as 'side1' | 'side2'].some(entry =>
          entry.perSet !== substitutePolicy.perSet ||
          entry.perMatch !== substitutePolicy.perMatch
        )
      );

      if (isDifferent) {
        for (const side of ['side1', 'side2'] as const) {
          substituteTrack[side] = substituteTrack[side].map(entry => ({
            ...entry,
            perSet: substitutePolicy.perSet || 0,
            perMatch: substitutePolicy.perMatch || 0,
          }));
        }
        this.match.substituteTrack = substituteTrack;
      }
    }
}
